import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import UserPicker from '../../components/UserPicker';
import {
    BindingRow,
    RoleInfo,
    createBinding,
    deleteBinding,
    listBindings,
} from '../../api/authz';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import { alertDialog, confirmDialog } from '../../utils/dialog';

/**
 * 角色成员面板 (2026-09-12): 在「角色管理」/「权限分配」里点一个角色 → 看 / 加 / 移除拥有该角色的用户.
 * 用户 · 角色 · 权限分配三处互通: 底层都是 rbac_role_binding, 改动后互相回读.
 *
 * 系统角色的"加 / 移除"有各自语义 (后端 rbac.CreateBinding / DeleteBinding 分派):
 *   普通用户 user  → 添加 = 管理员直接授予准入 (可输入任意公司邮箱); 移除 = 移出平台
 *   管理员 admin   → 添加 = 设为管理员 (只能选已准入用户); 移除 = 转为普通用户 (最后一个不可降)
 */
const STATUS_ZH: Record<string, string> = {
    pending: '待审批',
    approved: '已准入',
    rejected: '已驳回',
};

function RoleMembersModal({
    role,
    onClose,
    onChanged,
}: {
    role: RoleInfo | null;
    onClose: () => void;
    onChanged?: () => void;
}) {
    const [list, setList] = useState<BindingRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const isUserRole = role?.name === 'user';
    const isAdminRole = role?.name === 'admin';

    const load = useCallback(async () => {
        if (!role) return;
        setLoading(true);
        setError('');
        try {
            const res = await listBindings(undefined, role.name);
            setList(res.list || []);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [role]);

    useEffect(() => {
        setEmail('');
        load();
    }, [load]);

    const addLabel = isUserRole
        ? '直接授予准入'
        : isAdminRole
          ? '设为管理员'
          : '添加';
    const addHint = isUserRole
        ? '输入公司邮箱: 无需对方申请, 立即获得普通用户权限 (首次登录按邮箱匹配)'
        : isAdminRole
          ? '从已准入用户中选择, 设为管理员'
          : '从平台用户中选择';
    const removeLabel = isUserRole
        ? '移出平台'
        : isAdminRole
          ? '转为普通用户'
          : '移除';

    const onAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!role) return;
        const addr = email.trim();
        if (!addr) return;
        if (
            isUserRole &&
            !(await confirmDialog(`直接授予 ${addr} 平台准入 (普通用户权限)?`, {
                danger: false,
            }))
        )
            return;
        setBusy(true);
        try {
            await createBinding(addr, role.name);
            setEmail('');
            await load();
            onChanged?.();
        } catch (err) {
            alertDialog(
                `${addLabel}失败: ${err instanceof ApiError ? err.message : String(err)}`,
            );
        } finally {
            setBusy(false);
        }
    };

    const onRemove = async (row: BindingRow) => {
        if (!role) return;
        const who = row.displayName || row.subjectId;
        const msg =
            row.subjectKind === 'group'
                ? `确认移除用户组「${who}」的「${role.displayName}」角色?\n组内全部成员将同时失去该角色。`
                : isUserRole
                  ? `确认将 ${who} 移出平台?\n将删除该用户及其全部角色, 对方需重新申请准入。`
                  : isAdminRole
                    ? `确认将 ${who} 转为普通用户? 其准入保留, 但不再有管理员权限。`
                    : `确认移除 ${who} 的「${role.displayName}」角色?`;
        if (!(await confirmDialog(msg))) return;
        try {
            await deleteBinding(row.id);
            await load();
            onChanged?.();
        } catch (err) {
            alertDialog(
                `${removeLabel}失败: ${err instanceof ApiError ? err.message : String(err)}`,
            );
        }
    };

    return (
        <Modal
            open={!!role}
            title={role ? `「${role.displayName}」的成员` : ''}
            subtitle={role && <span className="font-mono">{role.name}</span>}
            onClose={onClose}
            width={680}
        >
            <div className="space-y-4">
                <form onSubmit={onAdd} className="flex items-end gap-2">
                    <label className="block flex-1">
                        <span className="mb-1 block text-xs text-slate-500">
                            {addHint}
                        </span>
                        {isUserRole ? (
                            <input
                                className="inp w-full"
                                placeholder="someone@makeblock.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        ) : (
                            <UserPicker
                                className="w-full"
                                value={email}
                                onChange={setEmail}
                            />
                        )}
                    </label>
                    <button
                        type="submit"
                        className="btn-primary whitespace-nowrap"
                        disabled={busy || !email.trim()}
                    >
                        {busy ? '处理中…' : addLabel}
                    </button>
                </form>
                {error && (
                    <div className="rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-600">
                        {error}
                    </div>
                )}
                <div className="rounded-lg border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="th">用户 / 用户组</th>
                                <th className="th">准入</th>
                                <th className="th">授予时间</th>
                                <th className="th w-28">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td
                                        className="td text-center text-slate-400"
                                        colSpan={4}
                                    >
                                        加载中…
                                    </td>
                                </tr>
                            )}
                            {!loading && list.length === 0 && (
                                <tr>
                                    <td
                                        className="td text-center text-slate-400"
                                        colSpan={4}
                                    >
                                        暂无成员
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                list.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-slate-50/60"
                                    >
                                        <td className="td">
                                            <div className="text-slate-800">
                                                {row.subjectKind ===
                                                    'group' && (
                                                    <span
                                                        className="tag mr-1.5 bg-violet-50 text-violet-700"
                                                        title="用户组: 组内全部成员都有该角色"
                                                    >
                                                        用户组
                                                    </span>
                                                )}
                                                {row.displayName || '—'}
                                            </div>
                                            <div className="font-mono text-xs text-slate-400">
                                                {row.subjectId}
                                            </div>
                                        </td>
                                        <td className="td text-xs">
                                            {row.subjectKind === 'group' ? (
                                                <span className="text-slate-400">
                                                    —
                                                </span>
                                            ) : row.accessStatus ? (
                                                <span
                                                    className={`tag ${row.accessStatus === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                                                >
                                                    {STATUS_ZH[
                                                        row.accessStatus
                                                    ] || row.accessStatus}
                                                </span>
                                            ) : (
                                                <span className="text-amber-600">
                                                    无用户记录
                                                </span>
                                            )}
                                        </td>
                                        <td className="td text-xs text-slate-500">
                                            {formatTime(row.createdAt)}
                                        </td>
                                        <td className="td">
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                onClick={() => onRemove(row)}
                                            >
                                                {removeLabel}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
}

export default RoleMembersModal;
